// src/components/MayaThorDoubleSwapGrid.tsx

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import Modal from 'react-modal';

import {
  assets,
  assetsInfo,
} from '../data/assetsData';
import { DoubleSwapQuoteResult } from '../types/SwapQuoteAndTrade';

// This tells us if we are running Locally or on Droplet 
// Value = "LOCAL" or "DROPLET"
// This is used currently to set the websocket
const MACHINE = process.env.REACT_APP_MACHINE;  

console.log (`Running on ${MACHINE} machine`);

if (!(MACHINE==="LOCAL" || MACHINE==="DROPLET")) {
  throw new Error('Missing environment variable for Machine.');
}

// This function calculates the most profitable swaps from our array.
// Currently set to select the top 3, it preps this informatoin for
// eventual display in our component.
const calculateTopResults = (results: DoubleSwapQuoteResult[][]) => {    

    let top3Results: DoubleSwapQuoteResult[] = [];
 
    results.forEach((column) => {
      column.forEach((cell) => {
        if (cell.arbitrageProfitUSD) {

          // Debugging info: 
          // console.log ("Inside calculateTopResults, Arbitrage Profit USD = ", cell.arbitrageProfitUSD)

          if (top3Results.length < 3) {
            top3Results.push(cell);
            top3Results.sort((a, b) => Number(b.arbitrageProfitUSD) - Number(a.arbitrageProfitUSD));
          } else {
            const minTopResult = top3Results[2];
            if (Number(cell.arbitrageProfitUSD) > Number(minTopResult.arbitrageProfitUSD)) {
              top3Results.pop();
              top3Results.push(cell);
              top3Results.sort((a, b) => Number(b.arbitrageProfitUSD) - Number(a.arbitrageProfitUSD));
            }
          }
        }
      });
    });

    return top3Results;
};  

// We use this function to retrieve from the Double Swap data, the amount of
// each coin that we are swapping from (the start amount of the swap.)
// This function retrieves the start amount info from the DoubleSwap data and stores it in a map
// which is later used by the component in the title line.
const retrieveSwapAmounts = (results: DoubleSwapQuoteResult[][]): Map<string, string> => {

  let swapAmounts = new Map<string, string>();

  for (let i = 0; i < results.length; i++) {
    for (let j = 0; j < results[i].length; j++) {
      let quoteResult = results[i][j];
      if (quoteResult.assetFrom !== null && quoteResult.sellAmountX !== null) {
        let assetTinyName = assetsInfo.getTinyNameByLongName(quoteResult.assetFrom);
        if (!swapAmounts.has(assetTinyName)) {
          swapAmounts.set(assetTinyName, quoteResult.sellAmountX);
        }
      }
    }
  }
  return swapAmounts;
}

// Set the details for how many rows and columns we are using in the display
const displayGridRows : number = (assets.length * 2);
const displayGridColumns : number = (assets.length);

// Set up the initial, null swap quote 2D array
const initialSwapQuoteArray: DoubleSwapQuoteResult[][] = (
  Array(displayGridRows)
    .fill(null)
    .map(() =>
      Array(displayGridColumns).fill({
        msgType: null, 	    
        quoteTime: null,               
        exchangeSellFrom: null,                    
        exchangeBuyBack: null, 	        
        assetFrom: null,
        assetTo: null,
        sellAmountX: null,
        buyBackAmountY: null,
        resultAmountZ: null,
        arbitrageProfit: null,
        arbitrageProfitUSD: null,
        sellSwapQuote: null,
        buybackSwapQuote: null,
        skip: true,
      })
    )
);

// Begin the component: MayaThorDoubleSwapGrid
const MayaThorDoubleSwapGrid: React.FC = () => {

  // set up the initial swap quote (null) array
  const [swapQuotes, setSwapQuotes] = useState<DoubleSwapQuoteResult[][]>(initialSwapQuoteArray)
  const [ws, setWs] = useState<WebSocket | null>(null);
  
  // Below is for the Modal box to view detailed content of the cell
  // State to manage the visibility of the Modal
  // State to manage the content of the Modal
  const [isModalOpen, setIsModalOpen] = useState(false);  
  const [modalContent, setModalContent] = useState<DoubleSwapQuoteResult | null>(null);
  const [showFullDoubleSwapQuote, setShowFullDoubleSwapQuote] = useState(false);

  const [statusMessage, setStatusMessage] = useState("");

  const websocketRef = useRef<WebSocket | null>(null);
  const connectionSuccessfulRef = useRef<boolean>(false);
  const reconnectManuallyRef = useRef<() => void>(() => {});

  // Function to handle when a cell is clicked
  const handleCellClick = (quoteResult: DoubleSwapQuoteResult) => {
    setModalContent(quoteResult);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  //  This useMemo hook calls the calculateTopResults function with the updated data whenever the data changes.
  //  ---> It was a useEffect call that didn't work so it has been changed.
  const topResults =  React.useMemo<DoubleSwapQuoteResult[]>(() => calculateTopResults(swapQuotes), [swapQuotes]);

  // Open and define our websocket
  // starting with variables for reconnect logic
  let reconnectAttempts = 0;
  let reconnectInterval: any = null;
  let manualClose = false; // Initialize flag
  connectionSuccessfulRef.current = false;

  useEffect(() => {

    // Initialize WebSocket connection
    // const websocket = new WebSocket('wss://localhost:8080');
    
    const connectWebSocket = () => {

      // if we are offline, send messages to the component and the console
      if (!navigator.onLine) {
        console.log('You appear to be offline. Reconnect manually.');
        setStatusMessage("You appear to be offline. Reconnect manually.");
      }
      else {

        // otherwise, connect/reconnect
        // while we deal with reconnection logic for when we disconnect
        if (reconnectAttempts < 3) {

          // close any dangling open sockets
          if (websocketRef.current && websocketRef.current.readyState < 2) {
            manualClose = true;
            websocketRef.current.close();
            connectionSuccessfulRef.current = false;             // Set the flag to true when connection is successful
          }

          // open our new websocket 
          let websocket : any;

          // on the local machine:
          if (MACHINE === "LOCAL") {
            // For local development, we can keep using ws:// on localhost
            websocket = new WebSocket('ws://localhost:8080');
          } else if (window.location.hostname === 'arb-bot-dev.warlocks.space') {
            // Development environment on server
            websocket = new WebSocket('wss://arb-bot-dev.warlocks.space/ws');
          } else {
            // Production environment
            websocket = new WebSocket('wss://arb.warlocks.space/ws');
          }

          websocketRef.current = websocket;

          // when we open the connection, print messages 
          // and set the reconnection logic
          websocket.onopen = () => {
            console.log('Connected to WebSocket server');
            setStatusMessage("Connected to Websocket Server...");
            reconnectAttempts = 0;                              // Reset the attempts on successful connection
            connectionSuccessfulRef.current = true;             // Set the flag to true when connection is successful
            if (reconnectInterval) {                            // If there is a reconnect interval clear it on successful connection
              clearInterval(reconnectInterval);
              reconnectInterval = null;
            }
            websocket.send('requestSwapQuoteData');             // Send a message to the server requesting initial data
          };

          // when we receive a message (which is swapData + isFresh from the backend)
          // we log the time to the status window
          // and populate the table with the results
          websocket.onmessage = (event : any) => {

            const payload = JSON.parse(event.data as string)
            const isFresh = payload.isFresh;
            const data : DoubleSwapQuoteResult[][] = payload.data; 

            const currentDate = new Date();
            
            // Retrieving time components
            const hours = currentDate.getHours();
            const minutes = currentDate.getMinutes();
            const seconds = currentDate.getSeconds();
            let minutesString = ""
            let secondsString = ""
          
            if (minutes < 10) { minutesString = "0" + minutes.toString() }
            else { minutesString = minutes.toString() }
            if (seconds < 10) { secondsString = "0" + seconds.toString() }
            else { secondsString = seconds.toString() }

            const formattedTime = `${hours}:${minutesString}:${secondsString}`;

            if (isFresh) {
              setStatusMessage(`Received New Data. Time is: ${formattedTime}`);
            } else {
              setStatusMessage(`Received Stale Data. Time is: ${formattedTime}`);
            }

            // const data : DoubleSwapQuoteResult[][] = JSON.parse(event.data as string);
            setSwapQuotes(data);
          };

          // error handling
          websocket.onerror = (error: any) => {
            console.error(`WebSocket error: ${error}`);
            setStatusMessage("Websocket error.  Check console.");
          };

          // when the websocket closes attempt a reconnection every minute
          // after 3 minutes, pause the reconnection for 10 minutes and try again
          websocket.onclose = (event: any) => {

            //debugging info
            // console.log('WebSocket connection closed with code:', event.code);
            if (event.wasClean) {
              console.log('WebSocket connection closed cleanly.');
            } else {
              console.log('WebSocket connection closed unexpectedly.');
            }
            console.log('WebSocket connection closed with reason:', event.reason);

            if (manualClose) {
              manualClose = false;
              return;
            }
            console.log('Disconnected from WebSocket server');
            setStatusMessage("Disconnected from WebSocket server.");
            connectionSuccessfulRef.current = false; // set this to false as connection is closed
            
            console.log ("reconnectInterval is", reconnectInterval);
            console.log ("reconnectAttempts is", reconnectAttempts);

            // Start reconnecting as soon as connection is lost
            if (!reconnectInterval) {
                //console.log ("got inside the first if loop.")
                reconnectInterval = setInterval(() => {
                    if (reconnectAttempts < 3) {
                        //console.log ("got inside the second if loop.")
                        console.log ("reconnectAttempts is", reconnectAttempts);

                        console.log('Attempting to reconnect...');
                        setStatusMessage("Attempting to reconnect once every minute.");
                        reconnectAttempts++;
                        connectWebSocket();
                    } else {
                        //console.log ("got inside the second else loop.")
                        console.log ("reconnectAttempts is", reconnectAttempts);

                        // If already attempted 3 times, wait for 10 minutes before next attempt
                        console.log('Waiting for 10 minutes before next reconnection attempt');
                        setStatusMessage('Waiting for 10 minutes before next reconnection attempt');
                        clearInterval(reconnectInterval);
                        reconnectInterval = null;
                        setTimeout(() => {
                            reconnectAttempts = 0; // reset the counter after pause
                            connectWebSocket();
                        }, 600000);  // 600000 ms = 10 minutes
                    }
                }, 60000); // attempt to reconnect every minute
            }
          };

          setWs(websocket);

        } 

      }

    };

    reconnectManuallyRef.current = () => {
      console.log ("Button Pressed")
      // Close the existing connection if it's open.
      if (websocketRef.current && websocketRef.current.readyState < 2) {
        websocketRef.current.close();;
      }
      // Reset reconnect attempts.
      reconnectAttempts = 0;
      // Establish a new connection.
      connectWebSocket();
    }; 

    connectWebSocket();

    // Cleanup function to close WebSocket when component unmounts
    return () => {
      if (websocketRef.current) {
      websocketRef.current.close();
      }
    };
   
  }, []);

  // Determine the Top 3 swaps from of the swapQuote Array
  // top3Array = calculateTopResults(newResults);
  let top3Array : DoubleSwapQuoteResult[] = [];
  calculateTopResults(swapQuotes);

  // Create the map which lets us know the swap amounts per coin
  // This is where we will pull in ideal swap amount data in future
  const swapAmountsMap = retrieveSwapAmounts(swapQuotes);

  // Now, the function to define the content for our grid, cell by cell
  const createGridContent = () => {

    const gridContent = new Array(displayGridRows + 1).fill(null).map(() => new Array(displayGridColumns + 1).fill(null));
   
    // Set all titles for the first row and first column
    for (let i = 0; i <= displayGridColumns; i++) {
      for (let j = 0; j <= displayGridRows; j++) {
  
        if (i === 0 && j === 0) {

          //First cell is blank
          gridContent[0][0] = <div className="grid-cell">&nbsp;</div>;
  
        } else if (i === 0 && j > 0 && assets[j - 1]) {
  
          // Set the row titles, two at a time (ie. Bitcoin. Bitcoin then Ethererum, Etherem)
          // There are two rows for each pair, which show the results wheb we start the 
          // swap 1) on Maya and the 2) on Thor
          gridContent[(2 * j) - 1][i] = <div className="grid-cell">{assets[j - 1].prettyname}</div>;
          gridContent[2 * j][i] = <div className="grid-cell">{assets[j - 1].prettyname}</div>;
  
        } else if (j === 0 && i > 0 && assets[i - 1]) {

          // Set the column titles and add the "amount" below each
          gridContent[j][i] = (
            <div className="grid-cell">
              {assets[i - 1].prettyname}&nbsp;&nbsp;&nbsp;&nbsp; <br />
              Swap: {swapAmountsMap.get(assets[i - 1].tinyName)}
            </div>
          );
        }
      } 
    }
  
    // Populate the rest of the grid based on swapResults

    // Iterate through columns, by row
    swapQuotes.forEach((column, i) => {
      column.forEach((result, j) => {

        // Display the results

        // If skip is set to false, then we will display
        // Note skip is set to true when there's a matching pair, like BTC/BTC, which will have no 
        // swap data as there is no swap.  In these cases, we print nothing

        if (!result.skip) {
          gridContent[j + 1][i + 1] = (
            <div
              className="grid-cell"
              onClick={() => handleCellClick(result)}  // Adding click event handler here
              style={{
                // if result exists and the profit is less than $-5, then change the color of the cell 
                // and color of the text
                backgroundColor: result && Number(result.arbitrageProfitUSD) > 0 ? "#FFD580" : "inherit",
                color: result && Number(result.arbitrageProfitUSD) > 0 ? "black" : "white",
              }}
            >
              {result.assetTo && result.assetFrom ? (
                <>
                  {result.exchangeSellFrom === "MAYA" ? "Maya" : "Thor"}: {assetsInfo.getTinyNameByLongName(result.assetFrom)} -&gt; {assetsInfo.getTinyNameByLongName(result.assetTo)}
                  <br />
                  {/* {Number(result.arbitrageProfit)}<br />  */}
                  ${Number(result.arbitrageProfitUSD).toFixed(4)}
                </>
               ) : (
                ""
            )} 
            </div>
          );
        }
      });
    });
  
    return gridContent;
  };
  

  // Call the createGridContent function to generate the grid content
  const content = createGridContent();

  return (
    <div className="container">
      {/* Add the title for the component.  Also add the execute button that will start our API Fetching */}
      <div className="header">
        <h2 className="text-white">Maya & Thor DoubleSwap Checker</h2>
      </div>
      <p>&nbsp;</p>

      {/* Create a flex container to hold the status window and the reconnect button */}
      <div className="flex-container">
        {/* Add the reconnect button */}
        <div className="button-container">
          {<button className="custom-button" onClick={reconnectManuallyRef.current}>Reconnect</button>}
        </div>
        {/* Place the status window beside the reconnect button */}
        <div className="status-window">
          {statusMessage}
        </div>
      </div>
      
      
      {/* Create the grid with the correct number of rows and columns */}
      <div className="crypto-grid">
        <table className="crypto-table">
          <tbody>
            {content.map((column, columnIndex) => (
              <tr key={columnIndex}>
                {column.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*    // At the end of the grid, create a new container containing the top 3 results. */}
      <div className="top-results">
        <h2>&nbsp;</h2>
        <h2 className="text-white">Top 3 Results</h2>
      </div>

      <div>
        {topResults.map((result, index) => {
          if (!result.skip) {
            // Don't render the list item if it's marked as skip (invalid or not fetched yet)
          return (
          <div key={index} className="result-item text-white">
            <p>#{index + 1}: From {assetsInfo.getTinyNameByLongName(result.assetFrom!)} to {assetsInfo.getTinyNameByLongName(result.assetTo!)}. Swap via {result.exchangeSellFrom} first. Profit(USD): {result.arbitrageProfitUSD}</p>
          </div>
          );
          } else return null }
          )
        }
      </div>

      {/* Modal pop-up info */}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Swap Quote Details">
        <button onClick={closeModal}>Close</button>
        <button onClick={() => setShowFullDoubleSwapQuote(!showFullDoubleSwapQuote)}>
          {showFullDoubleSwapQuote ? 'Hide Full Quote Data' : 'Show Full Quote Data'}
        </button>
        {modalContent && (
          <div>
          <h2>Details for {assetsInfo.getTinyNameByLongName(modalContent.assetFrom!)} to {assetsInfo.getTinyNameByLongName(modalContent.assetTo!)}</h2>
          {modalContent.sellSwapQuote && (
            <div>
              <h3>Sell Swap Quote</h3>
              <p>Expected Amount Out: {modalContent.sellSwapQuote.expected_amount_out}</p>
            </div>
          )}
          {modalContent.buybackSwapQuote && (
            <div>
              <h3>Buyback Swap Quote</h3>
              <p>Expected Amount Out: {modalContent.buybackSwapQuote.expected_amount_out}</p>
            </div>
          )}
          {showFullDoubleSwapQuote && (
            <div>
              <h3>Full Double Swap Quote Data</h3>
              <pre>{JSON.stringify(modalContent, null, 2)}</pre>
            </div>
          )}
        </div>
        )}
      </Modal>
        
  </div>

  );
};

export default MayaThorDoubleSwapGrid;